import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AdminPageShell } from '../components/AdminShared';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Search, Plus, Edit, Trash2, UserX, UserCheck, Shield } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const ROLES = ['admin', 'interpreter', 'client', 'org_admin', 'finance', 'hr'];
const ROLE_COLORS = {
  admin: 'bg-red-100 text-red-700', interpreter: 'bg-blue-100 text-blue-700',
  client: 'bg-green-100 text-green-700', org_admin: 'bg-purple-100 text-purple-700',
  finance: 'bg-amber-100 text-amber-700', hr: 'bg-teal-100 text-teal-700',
};
const ALL_PERMISSIONS = [
  'manage_users', 'manage_orgs', 'manage_billing', 'manage_leads',
  'manage_interpreters', 'manage_clients', 'manage_languages',
  'manage_language_requests', 'manage_invoices', 'manage_fee_schedules',
  'manage_pay_rates', 'view_reports', 'manage_settings',
];

function api(path, opts = {}) {
  const token = localStorage.getItem('token');
  return fetch(`${API}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...opts.headers },
  }).then(async r => {
    if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || r.statusText); }
    return r.json();
  });
}

export default function AdminUsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [dialog, setDialog] = useState(null); // null | 'create' | 'edit'
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', role: 'interpreter', org_id: '', password: '', permissions: [] });

  const fetchUsers = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterRole) params.set('role', filterRole);
      if (filterStatus) params.set('status', filterStatus);
      if (search) params.set('search', search);
      const data = await api(`/api/admin/users?${params}`);
      setUsers(data);
    } catch (e) { toast.error(e.message); }
    setLoading(false);
  }, [filterRole, filterStatus, search]);

  const fetchOrgs = useCallback(async () => {
    try { setOrgs(await api('/api/admin/orgs')); } catch {}
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);
  useEffect(() => { fetchOrgs(); }, [fetchOrgs]);

  const openCreate = () => {
    setForm({ name: '', email: '', role: 'interpreter', org_id: '', password: '', permissions: [] });
    setSelected(null);
    setDialog('create');
  };

  const openEdit = (u) => {
    setForm({ name: u.name || '', email: u.email, role: u.role, org_id: u.org_id || '', password: '', permissions: u.permissions || [] });
    setSelected(u);
    setDialog('edit');
  };

  const togglePerm = (perm) => {
    setForm(f => ({ ...f, permissions: f.permissions.includes(perm) ? f.permissions.filter(p => p !== perm) : [...f.permissions, perm] }));
  };

  const handleSave = async () => {
    try {
      if (dialog === 'create') {
        if (!form.email) { toast.error('Email is required'); return; }
        await api('/api/admin/users', { method: 'POST', body: JSON.stringify(form) });
        toast.success('User created');
      } else {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await api(`/api/admin/users/${selected.id}`, { method: 'PUT', body: JSON.stringify(payload) });
        toast.success('User updated');
      }
      setDialog(null);
      fetchUsers();
    } catch (e) { toast.error(e.message); }
  };

  const handleDeactivate = async (u) => {
    if (!window.confirm(`Deactivate ${u.name || u.email}?`)) return;
    try {
      await api(`/api/admin/users/${u.id}/deactivate`, { method: 'PATCH' });
      toast.success('User deactivated');
      fetchUsers();
    } catch (e) { toast.error(e.message); }
  };

  const handleReactivate = async (u) => {
    try {
      await api(`/api/admin/users/${u.id}/reactivate`, { method: 'PATCH' });
      toast.success('User reactivated');
      fetchUsers();
    } catch (e) { toast.error(e.message); }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`Permanently delete ${u.name || u.email}? This cannot be undone.`)) return;
    try {
      await api(`/api/admin/users/${u.id}`, { method: 'DELETE' });
      toast.success('User deleted');
      fetchUsers();
    } catch (e) { toast.error(e.message); }
  };

  const counts = { total: users.length, active: users.filter(u => !u.deactivated).length, deactivated: users.filter(u => u.deactivated).length };

  return (
    <AdminPageShell title="User Management" subtitle={`${counts.total} users (${counts.active} active)`} loading={loading}
      actions={<Button onClick={openCreate} data-testid="create-user-btn"><Plus className="w-4 h-4 mr-1" />Add User</Button>}
      testId="admin-users-page">

      <div className="space-y-4">
        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search name or email..." className="pl-9 bg-white text-slate-900 border-slate-300" value={search} onChange={e => setSearch(e.target.value)} data-testid="users-search" />
          </div>
          <select value={filterRole} onChange={e => setFilterRole(e.target.value)} className="h-10 px-3 border border-slate-300 rounded-md text-sm bg-white text-slate-900" data-testid="users-filter-role">
            <option value="">All Roles</option>
            {ROLES.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1).replace('_', ' ')}</option>)}
          </select>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="h-10 px-3 border border-slate-300 rounded-md text-sm bg-white text-slate-900" data-testid="users-filter-status">
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="deactivated">Deactivated</option>
          </select>
        </div>

        {/* Table */}
        <div className="bg-white border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-slate-900">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-slate-700">Name</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-700">Email</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-700">Role</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-700">Organization</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-700">Permissions</th>
                  <th className="text-left px-4 py-3 font-medium text-slate-700">Status</th>
                  <th className="text-right px-4 py-3 font-medium text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {users.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-8 text-slate-500">No users found</td></tr>
                ) : users.map(u => (
                  <tr key={u.id} className={`hover:bg-slate-50 ${u.deactivated ? 'opacity-50' : ''}`} data-testid={`user-row-${u.id}`}>
                    <td className="px-4 py-3 font-medium text-slate-900">{u.name || '-'}</td>
                    <td className="px-4 py-3 text-slate-600">{u.email}</td>
                    <td className="px-4 py-3"><span className={`text-xs font-medium px-2 py-1 rounded-full ${ROLE_COLORS[u.role] || 'bg-gray-100 text-gray-700'}`}>{u.role?.replace('_', ' ')}</span></td>
                    <td className="px-4 py-3 text-slate-600">{u.org_name || '-'}</td>
                    <td className="px-4 py-3">
                      {(u.permissions || []).length > 0
                        ? <span className="text-xs text-slate-500">{u.permissions.length} permissions</span>
                        : <span className="text-xs text-slate-400">-</span>}
                    </td>
                    <td className="px-4 py-3">
                      {u.deactivated
                        ? <span className="text-xs font-medium px-2 py-1 rounded-full bg-red-100 text-red-700">Deactivated</span>
                        : <span className="text-xs font-medium px-2 py-1 rounded-full bg-green-100 text-green-700">Active</span>}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(u)} data-testid={`user-edit-${u.id}`}><Edit className="w-4 h-4" /></Button>
                      {u.deactivated
                        ? <Button variant="ghost" size="sm" onClick={() => handleReactivate(u)} title="Reactivate" data-testid={`user-reactivate-${u.id}`}><UserCheck className="w-4 h-4 text-green-600" /></Button>
                        : <Button variant="ghost" size="sm" onClick={() => handleDeactivate(u)} title="Deactivate" data-testid={`user-deactivate-${u.id}`}><UserX className="w-4 h-4 text-amber-600" /></Button>}
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(u)} data-testid={`user-delete-${u.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={!!dialog} onOpenChange={open => { if (!open) setDialog(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{dialog === 'create' ? 'Add New User' : 'Edit User'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4" data-testid="user-dialog">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm text-slate-700">Full Name *</Label>
                <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="mt-1" data-testid="user-form-name" />
              </div>
              <div>
                <Label className="text-sm text-slate-700">Email *</Label>
                <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="mt-1" data-testid="user-form-email" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm text-slate-700">Role</Label>
                <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} className="w-full mt-1 h-10 px-3 border rounded-md text-sm" data-testid="user-form-role">
                  {ROLES.map(r => <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1).replace('_', ' ')}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-sm text-slate-700">Organization</Label>
                <select value={form.org_id} onChange={e => setForm(f => ({ ...f, org_id: e.target.value }))} className="w-full mt-1 h-10 px-3 border rounded-md text-sm" data-testid="user-form-org">
                  <option value="">No organization</option>
                  {orgs.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label className="text-sm text-slate-700">{dialog === 'create' ? 'Password *' : 'New Password (leave blank to keep current)'}</Label>
              <Input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} className="mt-1" placeholder={dialog === 'edit' ? 'Leave blank to keep current' : ''} data-testid="user-form-password" />
            </div>
            <div>
              <Label className="text-sm text-slate-700 flex items-center gap-1"><Shield className="w-3 h-3" />Admin Permissions</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {ALL_PERMISSIONS.map(p => (
                  <label key={p} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`perm-${p}`}>
                    <input type="checkbox" checked={form.permissions.includes(p)} onChange={() => togglePerm(p)} className="rounded" />
                    <span className="text-slate-700">{p.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
              <Button onClick={handleSave} data-testid="user-form-save">{dialog === 'create' ? 'Create User' : 'Save Changes'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AdminPageShell>
  );
}
